import makeStyles from '@mui/styles/makeStyles';
import React, { useState } from 'react';
import {
  Fab,
  Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material';
import { Download } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from '../common/components/LocalizationProvider';
import { useCatch, useEffectAsync } from '../reactHelper';
import PageLayout from '../common/components/PageLayout';
import SettingsMenu from './components/SettingsMenu';
import CollectionActions from './components/CollectionActions';
import TableShimmer from '../common/components/TableShimmer';

const useStyles = makeStyles((theme) => ({
  columnAction: {
    width: '1%',
    paddingRight: theme.spacing(1),
  },
  fab: {
    position: 'fixed',
    bottom: theme.spacing(2),
    right: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      bottom: `calc(${theme.dimensions.bottomBarHeight}px + ${theme.spacing(2)})`,
    },
  },
}));

const FilesPage = () => {
  const classes = useStyles();
  const t = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [timestamp, setTimestamp] = useState(Date.now());
  const fileUpload = React.createRef();

  const handleFileDownload = useCatch(async (fileName) => {
    const response = await fetch(`/api/file/${fileName}`);
    if (response.ok) {
      const url = window.URL.createObjectURL(
        new Blob([await response.blob()]),
      );
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        fileName,
      );
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } else {
      throw Error(await response.text());
    }
  });

  const handleFile = async (event) => {
    const files = Array.from(event.target.files);
    const formData = new FormData();
    formData.append('file', files[0]);
    const response = await fetch('/api/file', {
      method: 'POST',
      body: formData,
    });
    if (!response.ok) {
      throw Error(await response.text());
    }
    setTimestamp(Date.now());
  };

  const downloadFileAction = {
    title: t('download'),
    icon: (<Download fontSize="small" />),
    handler: handleFileDownload,
  };

  useEffectAsync(async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/file/names');
      if (response.ok) {
        setItems(await response.json());
      } else {
        throw Error(await response.text());
      }
    } finally {
      setLoading(false);
    }
  }, [timestamp]);

  return (
    <PageLayout menu={<SettingsMenu />} breadcrumbs={['settingsTitle', 'files']}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>{t('fileName')}</TableCell>
            <TableCell className={classes.columnAction} />
          </TableRow>
        </TableHead>
        <TableBody>
          {!loading ? items.map((item) => (
            <TableRow key={item}>
              <TableCell>{item}</TableCell>
              <TableCell className={classes.columnAction} padding="none">
                <CollectionActions itemId={item} endpoint="file" hideEdit="true" customAction={downloadFileAction} setTimestamp={setTimestamp} />
              </TableCell>
            </TableRow>
          )) : (<TableShimmer columns={3} endAction />)}
        </TableBody>
      </Table>
      <Fab size="medium" color="primary" className={classes.fab} onClick={() => { fileUpload.current.click(); }}>
        <input ref={fileUpload} hidden accept="*" type="file" onChange={handleFile} />
        <AddIcon />
      </Fab>
    </PageLayout>
  );
};

export default FilesPage;
